// © Microsoft Corporation. All rights reserved.

import "../publicPath";

import {
    ContentActionsToolbar,
    ContentActionsToolbarConfig,
    ContentActionsToolbarConnector,
    ContentActionsToolbarReducer,
    PlaceHolderId
} from "@msnews/content-actions-toolbar";
import { ExperienceConfigWrapper, TelemetryBase, appEnvironment, resolver } from "@msnews/core";
import { ExperienceInitialProps, Telemetry } from "@msnews/experiences-base";
import { handleAppErrorEventCall, handlePartialPageImpressionCall, handleTelemetryCall } from "../integration/PrimeTelemetry";

import { ContentActionsToolbarAppErrors } from "@msnews/diagnostics";
import { DesignSystemDefaults } from "@microsoft/fast-components-styles-msft";
import { DesignSystemProvider } from "@microsoft/fast-jss-manager-react";
import { Mediator } from "../integration/Mediator";
import { PageBase } from "@msnews/experiences-redux";
import React from "react";
import { SharedDocumentType } from "@msnews/views-helpers";
import { ToolbarPosition } from "@msnews/content-actions-toolbar";
import { isNullOrUndefined } from "util";

// export default function bootstrapPrimeHybrid(): void {
// Get an instance of the PageBase for this app, and start the app
const app = PageBase.getInstance();

// Mock documentId to be used when running hybrid locally for testing purposes
const mockDocumentId = "BBKJJOS";

// Map base telemetry to prime library for hybrid
TelemetryBase.trackAppErrorEvent = handleAppErrorEventCall;
TelemetryBase.trackPartialImpression = handlePartialPageImpressionCall;
TelemetryBase.trackEvent = handleTelemetryCall;

// Start the App
app.start();

let hostElement: HTMLElement;

const appTelemetry = Telemetry.createRootProps("spaContentActionsToolbarExperienceContainer", undefined, appEnvironment.RootTelemetryId);
const initialProps: ExperienceInitialProps = {
    contentId: mockDocumentId,
    parentTelemetry: appTelemetry,
    context: {
        handleTelemetryCall: handleTelemetryCall
    }
};

/**
 * Otains document data properties from Prime placeholder and sets up the initial props and appEnv variables
 */
function setupContentActionsInitialState(): void {
    // For Content Actions the placeholder can be in vertical in one prime region or horizontal in another prime region (depending on pagetype and column size)
    hostElement = document.getElementById(PlaceHolderId.vertical) || document.getElementById(PlaceHolderId.horizontal) || document.getElementById("content-actions-toolbar");

    const dataset = hostElement.dataset || {};
    const cmsDocumentId: string = dataset.documentid || mockDocumentId;
    const isRSThree: string = dataset.isrsthree || "false";

    // Check for the existence of second position and sets staticsPosition accordingly
    var staticPosition = hostElement.id === PlaceHolderId.vertical ?
        document.getElementById(PlaceHolderId.horizontal) ? undefined : ToolbarPosition.vertical :
        document.getElementById(PlaceHolderId.vertical) ? undefined : ToolbarPosition.horizontal;

    // Set initital props
    initialProps.contentId = cmsDocumentId;
    initialProps.context.staticPosition = staticPosition;
    initialProps.context.isRSThree = (isRSThree.toLowerCase() === "true");
    initialProps.context.documentType = SharedDocumentType.CMS;

    // Update app environment
    appEnvironment.HostPage = {
        originalUrl: location.href,
        hostName: location.hostname,
        verticalKey: dataset.verticalkey || "news",
        categoryKey: dataset.categorykey || "other",
        topDomain: appEnvironment.HostPage.topDomain
    };
}

/**
 * Connected the component to the Redux store passing initial props data
 * @param {any} experienceConfig
 * @returns The Redux connected component
 */
function connectContentActionsComponent(contentActionsToolbarConfig: ContentActionsToolbarConfig): Promise<React.ComponentType<{}>> {
    const contentActionsToolbarConnector = new ContentActionsToolbarConnector("ContentActionsToolbar",
                                                                              "",
                                                                              app.rootReducer,
                                                                              new ContentActionsToolbarReducer(),
                                                                              app.store,
                                                                              contentActionsToolbarConfig);
    return contentActionsToolbarConnector.connectComponent(ContentActionsToolbar, initialProps);
}

/**
 * Render the experience on hostElement placeholder
 * @param contentActionsExperience
 */
function renderExperience(contentActionsExperience: any): void {
    app.renderRootElement(
        React.createElement(
            DesignSystemProvider,
            { designSystem: DesignSystemDefaults },
            React.createElement(contentActionsExperience)
        ),
        hostElement
    );
}

let contentActionsConfig: ContentActionsToolbarConfig;
const configFilePath = "cms/api/amp/experienceConfigIndex/BBWebZe";
resolver.getConfig(configFilePath)
    .then((experienceConfig: ExperienceConfigWrapper) => {
        if (!experienceConfig) {
            throw Error(`Failed to load config file. Path: ${configFilePath}`);
        }
        contentActionsConfig = experienceConfig.properties;
        setupContentActionsInitialState();
        return connectContentActionsComponent(contentActionsConfig);
    }).then(renderExperience)
        .catch(error => {
            handleAppErrorEventCall({
                ...ContentActionsToolbarAppErrors.ContentActionsExperienceLoadError,
                message: `Error in hybrid loading Content Actions experience`,
                pb: {
                    ...ContentActionsToolbarAppErrors.ContentActionsExperienceLoadError.pb,
                    customMessage:  `Url: ${location.href} Config: ${contentActionsConfig} Error: ${error}`
                }
            });
            return;
        }
    );

// Listen to update events from Prime (Necessary as video page auto updates current displayed video)
Mediator.registerContentActionsUpdatedEvent(async () => {
    setupContentActionsInitialState();
    connectContentActionsComponent(contentActionsConfig)
        .then(renderExperience);
});